import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { iso6391, Language, LogService, Project } from 'htv-lib'
import languageList from '../../../../htv-lib/src/assets/data/language.json'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { PlatformService } from '../platforms/platform.service'
import { StorageService } from './storage.service'

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  isLoading$ = new BehaviorSubject<boolean>(true)

  readonly defaultLanguage: Language = { code: 'fr', countryCode: 'fr-FR', name: 'French', nativeName: 'Français' }
  readonly completeLanguageList: Language[] = languageList

  projectLanguage?: Language
  supportedLanguages = ['fr', 'en']
  availableLanguageList: Language[] = []

  selectedLanguage?: Language

  savedLanguage?: Language
  savedLanguageCode?: string
  readonly savedLanguageCodeKey = 'Language-savedLanguageCode'

  constructor(
    private logService: LogService,
    private platformService: PlatformService,
    private storageService: StorageService,
    private translateService: TranslateService
  ) {
    // do nothing
  }

  init(): void {
    this.logService.info(`[LanguageService] Initialization`)

    this.savedLanguageCode = this.getSavedLanguageCode()
    this.logService.info(`[LanguageService] savedLanguageCode = ${this.savedLanguageCode}`)

    if (this.savedLanguageCode) {
      this.savedLanguage = this.getLanguageFromCode(this.savedLanguageCode)

      if (this.savedLanguage) {
        this.translateService.setDefaultLang(this.savedLanguageCode)
        this.setSelectedLanguage(this.savedLanguage)
        return
      } else {
        this.logService.error(`[LanguageService] Could not find language for saved code ${this.savedLanguageCode}`)
      }
    }

    this.translateService.setDefaultLang(this.defaultLanguage.code)
    this.setSelectedLanguage(this.defaultLanguage, true)
  }

  initFromProject(project: Project): void {
    this.isLoading$.next(true)
    this.logService.debug('[LanguageService] Initializing with Humecloud data')

    const { generalConfig } = project.configuration
    const configLanguage = this.getLanguageFromCode(generalConfig.defaultLanguage)
    this.projectLanguage = configLanguage
    this.supportedLanguages = generalConfig.supportedLanguages
    this.availableLanguageList = this.completeLanguageList.filter(lang => this.supportedLanguages.includes(lang.code))

    if (!this.savedLanguage) {
      this.setSelectedLanguage(configLanguage || this.defaultLanguage)
    }

    this.isLoading$.next(false)
  }

  getLanguageFromCode(code: string): Language | undefined {
    return this.completeLanguageList.find(lang => lang.code === code)
  }

  async setSelectedLanguage(language: Language, skipSaving = false): Promise<void> {
    if (this.selectedLanguage && this.selectedLanguage.code == language.code) {
      this.logService.debug(`[LanguageService] Already using language: ${language.code}`)
      return
    }

    this.logService.info(`[LanguageService] Selected language: ${language.code}`)

    this.selectedLanguage = language
    if (!skipSaving) {
      this.setSavedLanguageCode(this.selectedLanguage)
    }

    this.translateService.use(this.selectedLanguage.code)
    moment.locale(this.selectedLanguage.code)
    await this.platformService.setLanguage(this.selectedLanguage.countryCode)
  }

  setSelectedLanguageFromCode(code: string): void {
    const language = this.getLanguageFromCode(code)
    if (language) {
      this.setSelectedLanguage(language)
    }
  }

  getSelectedLanguage(): Language {
    return this.selectedLanguage ?? this.defaultLanguage
  }

  getNativeLanguage(code: string): string {
    switch (code) {
      case undefined:
      case 'none':
      case 'yyy':
        return this.translateService.instant('CHANNEL_SUBTITLE.NONE')
      case '':
        return this.translateService.instant('CHANNEL_SUBTITLE.DEFAULT')
      case 'qaa':
        return this.translateService.instant('CHANNEL_SUBTITLE.ORIGINAL_VERSION.FULL')
      case 'qad':
        return this.translateService.instant('CHANNEL_SUBTITLE.AUDIO_DESCRIPTION.FULL')
      default: {
        const lang = code.split('-')[0]
        return iso6391[lang]
      }
    }
  }

  setSavedLanguageCode(language?: Language): void {
    this.savedLanguage = language
    this.savedLanguageCode = language?.code
    if (this.savedLanguageCode) {
      this.storageService.saveString(this.savedLanguageCodeKey, this.savedLanguageCode)
    } else {
      this.storageService.removeValue(this.savedLanguageCodeKey)
    }
  }

  getSavedLanguageCode(): string | undefined {
    return this.storageService.getString(this.savedLanguageCodeKey) ?? undefined
  }

  clearGuestData(): void {
    this.setSavedLanguageCode(undefined)
  }
}
